<template>
    <div class="container min-full d-flex flex-column align-items-center justify-content-center">
        <LoadingSpinner v-if="loading" />
        <transition
            v-else
            name="fade"
            mode="out-in"
        >
            <AssessmentAgreement
                v-if="section == 'agreement'"
                :title="assessment.name"
                :loading="loading"
                @proceed="proceed"
                @end="end"
            />
            <AssessmentPermissions
                v-else-if="section == 'permissions'"
                :permissionsStatus="permissionsStatus"
                :cameras="cameras"
                :selectedCamera="selectedCamera"
                :pending="pending"
                :cameraStream="cameraStream"
                :cameraFailMessage="cameraFailureMessage"
                @proceed="proceed"
                @browser="browser"
                @screenCheck="screenCheck"
                @capture="capture"
                @camera="camera"
                @fullscreen="fullscreen"
                @cameraChange="cameraChange"
            />
            <AssessmentAI
                v-else-if="section == 'ai'"
                :cameraStream="cameraStream"
                :error="aiStartError"
                :ready="aiStartReady"
                :baseFace="aiBaseFace"
                @capture="baseFace"
                @proceed="proceed"
            />
            <AssessmentPopup
                v-else-if="section == 'popup'"
                @proceed="proceed"
            />
            <AssessmentIntro
                v-else-if="section == 'intro'"
                :title="assessment.name"
                :intro="assessment.intro"
                :textDir="direction"
                :duration="duration"
                :loading="loading"
                @start="start"
                @proceed="proceed"
            />
            <AssessmentExam
                v-else-if="section == 'exam'"
                :assessment="assessment"
                :textDir="direction"
                :time="time"
                :question="question"
                :lastQuestion="lastQuestion"
                :seenAll="seenAll"
                :showNav="showNav"
                :startTime="startTime"
                :cameraStream="cameraStream"
                @toggleNav="toggleNav"
                @goBack="goBack"
                @goForward="goForward"
                @goToQuestion="goToQuestion"
                @proceed="proceed"
                @submit="completeAssessmentSubmission"
                @response="response"
                @cameraFail="cameraIssue"
                @startAI="startAI"
            />
            <AssessmentOutro
                v-else-if="section == 'outro'"
                :outro="assessment.outro"
                :textDir="direction"
                :expired="expired"
                :loading="submitting"
                :submitted="submissionComplete"
                :submitError="submissionError"
                @proceed="proceed"
                @retry="retryAssessmentSubmission"
                @finish="finish"
            />
        </transition>
        <b-modal
            :title="$t('exited')"
            :visible="exited"
            ok-only
            ok-title="Close"
            no-close-on-backdrop
            no-close-on-esc
            hide-header-close
            centered
            @ok="finish()"
            >{{ $t('security-issue') }}
        </b-modal>
    </div>
</template>
<script>
import AssessmentService from '../services/AssessmentService';
import AssessmentAgreement from '../components/Assessment/AssessmentAgreement';
import AssessmentPermissions from '../components/Assessment/AssessmentPermissions';
import AssessmentAI from '@/components/Assessment/AssessmentAI.vue';
import AssessmentPopup from '@/components/Assessment/AssessmentPopup';
import AssessmentIntro from '../components/Assessment/AssessmentIntro';
import AssessmentExam from '../components/Assessment/AssessmentExam';
import AssessmentOutro from '../components/Assessment/AssessmentOutro';

import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';
import JsZip from 'jszip';
import checkTime from '../common/getCheckTime';
import LoadingSpinner from '../components/loader/Loader';
import ReadableDuration from '@/common/getReadableDuration';
import Bugsnag from '@bugsnag/js';
import { AiService } from '@/common/ai';
import getAssessmentBrowserSupport from '@/common/getAssessmentBrowserSupport';
import { getDetailNetworkToast, notifyError } from '@/common/network';

export default {
    name: 'AssessmentScreen',
    components: {
        AssessmentAgreement,
        AssessmentPermissions,
        AssessmentAI,
        AssessmentPopup,
        AssessmentIntro,
        AssessmentExam,
        AssessmentOutro,
        LoadingSpinner,
    },
    computed: {
        ...mapGetters({
            permissions: 'getPermissions',
        }),
        screenCaptureEnabled() {
            return this.$store.getters.getCaptureScreens && this.$store.getters.getAI === false;
        },
        direction() {
            if (this.assessment.is_rtl !== undefined) {
                if (this.assessment.is_rtl) {
                    return 'rtl';
                } else {
                    return 'ltr';
                }
            } else {
                return '';
            }
        },
    },
    data() {
        return {
            assessment: {},
            section: null,
            permissionsStatus: false,
            duration: null,
            assessmentDate: null,
            screenStream: null,
            screenTrack: null,
            cameraStream: null,
            cameraImageCapture: null,
            interval: null,
            securityCheckInterval: null,
            securityCheckInFlight: false,
            zip: new JsZip(),
            prevP: null,
            time: null,
            startTime: null,
            loading: true,
            assessmentStarted: false,
            question: 0,
            lastQuestion: 0,
            seenAll: false,
            showNav: false,
            submitting: false,
            expired: false,
            wakeLock: null,
            exited: false,
            ended: false,
            reason: '',
            submissionComplete: false,
            submissionError: false,
            submitResponse: null,
            failureToSubmitMarked: false,
            screens: null,
            windowManagementPermissionStatus: null,
            user: this.$store.getters.getUser,
            cameraCount: 0,
            cameraConstraints: null,
            selectedCamera: null,
            cameras: [],
            cameraFailureMessage: null,
            pending: {},
            captureEvidence: true,
            evidenceGap: 30000,
            evidenceCapturedCount: {
                camera: 0,
                screen: 0,
            },
            aiService: this.$store.getters.getSecureAssessment && this.$store.getters.getAI ? new AiService() : null,
            aiStartReady: false,
            aiStartError: null,
            aiBaseFace: null,
            aiFlags: [],
        };
    },
    mounted() {
        this.$store.dispatch('resetPermissions');
        if (this.$store.getters.getSecureAssessment && (this.$store.getters.getCapturePhotos || this.screenCaptureEnabled) && this.$store.getters.getAI === false) {
            this.captureEvidence = true;
        } else {
            this.captureEvidence = false;
        }
        AssessmentService.getAssessment(this.$route.params.id)
            .then((response) => {
                const assessment = this.getValidAssessment(response);
                if (!assessment) {
                    return;
                }

                if (this.$store.state.site.id === 0) {
                    const excludedProviderIds = require('sites-config').excludedProviderIds || [];
                    if (excludedProviderIds.includes(assessment.provider_id)) {
                        this.$router.push('/assessments');
                        return;
                    }
                } else {
                    if (this.$store.state.site.id !== assessment.provider_id) {
                        this.$router.push('/assessments');
                        return;
                    }
                }

                this.assessment = assessment;

                document.title = this.assessment.name + ' | ' + this.$store.state.site.shortTitle;
                this.duration = ReadableDuration.getReadableDuration(this.assessment.duration);
                if (this.duration) {
                    switch (this.duration[1]) {
                        case 'hours':
                            this.time = 1000 * 60 * 60 * this.duration[0];
                            break;
                        case 'hour':
                            this.time = 1000 * 60 * 60;
                            break;
                        case 'minutes':
                            this.time = 1000 * 60 * this.duration[0];
                            break;
                        case 'minute':
                            this.time = 1000 * 60;
                            break;
                    }
                }

                if (!this.$store.state.settings.secure_assessments && this.assessment.status === 'Started') {
                    this.start();
                } else if (this.$store.state.site.assessmentAgreement) {
                    this.section = 'agreement';
                } else if (this.$store.state.settings.secure_assessments) {
                    this.section = 'permissions';
                } else {
                    this.section = 'intro';
                }

                this.loading = false;
                if (this.$store.getters.getSecureAssessment) {
                    this.startSecurityMonitor();
                }
                if ((this.assessment.status == 'Enrolled' || (!this.$store.getters.getSecureAssessment && this.assessment.status == 'Started')) && this.assessment.trigger_prop) {
                    const date = DateTime.fromISO(this.assessment.trigger_prop);
                    const timestamp = parseInt(date.toFormat('x'));
                    const checkWindow = checkTime.getOutsideWindow(timestamp, {
                        days: 30,
                    });
                    if (checkWindow) {
                        this.$router.push('/assessments');
                    }
                } else {
                    this.$router.push('/assessments');
                }
            })
            .catch((error) => {
                notifyError(error, {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'mounted.getAssessment',
                        assessmentId: this.$route.params.id,
                    },
                });
                this.showLoadFailureToast();
                this.$router.push('/assessments');
            });
    },
    beforeDestroy() {
        this.stopSecurityMonitor();
        this.markFailureToSubmit();
        if (!this.ended) {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            }
            if (this.cameraStream) {
                this.cameraStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            if (this.screenStream) {
                this.screenStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            this.$store.dispatch('resetPermissions');
        }
    },
    watch: {
        permissions: {
            deep: true,
            handler(permissions) {
                if (!this.$store.getters.getSecureAssessment) {
                    this.permissionsStatus = true;
                } else {
                    for (const permission in permissions) {
                        if (permission === 'camera' && !this.$store.getters.getCapturePhotos && !this.$store.getters.getAI) {
                            continue;
                        } else if (permission === 'capture' && !this.screenCaptureEnabled) {
                            continue;
                        } else if (!permissions[permission]) {
                            this.permissionsStatus = false;
                            if (this.section === 'exam' && !this.ended) {
                                this.examFailure();
                            } else if (this.section === 'intro') {
                                this.section = 'permissions';
                            }
                            return false;
                        }
                    }
                    this.permissionsStatus = true;
                }
            },
        },
        'aiService.flags': {
            deep: true,
            handler(flags) {
                if (flags) {
                    this.aiFlags = [...this.aiService.flags];
                    this.flagItUp(flags[flags.length - 1]);
                }
            },
        },
    },
    methods: {
        syncAssessmentProgressState() {
            const totalQuestions = Array.isArray(this.assessment?.blocks) ? this.assessment.blocks.length : 0;

            if (totalQuestions === 0) {
                this.lastQuestion = 0;
                this.seenAll = false;
                return;
            }

            if (this.question > this.lastQuestion) {
                this.lastQuestion = this.question;
            }

            this.seenAll = this.lastQuestion >= totalQuestions - 1;
        },
        showToast(titleKey, textKey, options = {}) {
            this.$bvToast.toast(this.$t(textKey, options.textArgs || []), {
                title: this.$t(titleKey, options.titleArgs || []),
                toaster: 'b-toaster-bottom-right',
                autoHideDelay: options.autoHideDelay || 10000,
                appendToast: false,
                variant: options.variant || 'primary',
                solid: true,
                id: options.id,
            });
        },
        showErrorToast(titleKey, textKey, options = {}) {
            this.showToast(titleKey, textKey, {
                ...options,
                variant: 'danger',
            });
        },
        showLoadFailureToast() {
            const toastKey = getDetailNetworkToast('assessment');
            this.showErrorToast(toastKey + 'Title', toastKey + 'Text');
        },
        showAiSetupFailureToast() {
            this.showErrorToast('aiSetupFailedTitle', 'aiSetupFailedText');
        },
        isExpectedPermissionDenial(error) {
            const errorName = error?.name;
            const errorMessage = typeof error?.message === 'string' ? error.message.toLowerCase() : '';

            return errorName === 'NotAllowedError'
                || errorName === 'PermissionDeniedError'
                || (errorName === 'SecurityError' && errorMessage.includes('permission'));
        },
        isExpectedCameraUnavailable(error) {
            const errorName = error?.name;
            const errorMessage = typeof error?.message === 'string' ? error.message.toLowerCase() : '';

            return errorName === 'NotReadableError'
                || errorName === 'TrackStartError'
                || (errorName === 'AbortError' && (
                    errorMessage.includes('could not start video source')
                    || errorMessage.includes('device in use')
                    || errorMessage.includes('starting video input')
                ));
        },
        toastCameraUnavailable() {
            this.showErrorToast('cameraUnavailableTitle', 'cameraUnavailableText');
        },
        handleCameraAccessFailure(error) {
            this.pending = {
                ...this.pending,
                camera: false,
            };
            this.cameraStream = null;
            this.cameraImageCapture = null;

            if (error?.name === 'NotFoundError') {
                this.selectedCamera = null;
                this.cameras = [];
            } else if (this.isExpectedCameraUnavailable(error)) {
                this.cameraFailureMessage = this.$t('cameraUnavailableText');
                this.toastCameraUnavailable();
            } else if (!this.isExpectedPermissionDenial(error)) {
                Bugsnag.notify(error instanceof Error ? error : new Error(error));
            }

            this.$store.dispatch('permissionChanged', ['camera', false]);
        },
        proceed(to, expired) {
            if (expired) {
                this.expired = true;
            }
            if (to === 'outro') {
                this.completeAssessmentSubmission();
                return;
            }
            this.section = to;
        },
        browser() {
            const browserSupport = getAssessmentBrowserSupport({
                capturePhotos: this.$store.getters.getCapturePhotos,
                captureScreens: this.screenCaptureEnabled,
                ai: this.$store.getters.getAI,
            });

            this.$store.dispatch('permissionChanged', ['browser', browserSupport.supported]);
        },
        startSecurityMonitor() {
            this.stopSecurityMonitor();
            this.runSecurityChecks();
            this.securityCheckInterval = setInterval(() => {
                this.runSecurityChecks();
            }, 10000);
        },
        stopSecurityMonitor() {
            if (this.securityCheckInterval) {
                clearInterval(this.securityCheckInterval);
                this.securityCheckInterval = null;
            }
        },
        async runSecurityChecks() {
            if (this.securityCheckInFlight || this.ended) {
                return;
            }
            this.securityCheckInFlight = true;
            try {
                this.checkFullscreenPermission();
                this.checkCapturePermission();
                this.checkCameraPermission();
                if (this.screens) {
                    await this.screenCheck(false);
                }
            } finally {
                this.securityCheckInFlight = false;
            }
        },
        async screenCheck(showPending = true) {
            if (showPending) {
                this.pending = {
                    ...this.pending,
                    screens: true,
                };
            }
            try {
                const screenDetails = await window.getScreenDetails();
                if (this.screens !== screenDetails) {
                    if (this.screens) {
                        this.screens.removeEventListener('screenschange', this.screenChange);
                    }
                    this.screens = screenDetails;
                    this.screens.addEventListener('screenschange', this.screenChange);
                }
                if (showPending) {
                    this.pending = {
                        ...this.pending,
                        screens: false,
                    };
                }
                if (this.screens.screens.length === 1 || process.env.VUE_APP_STAGE === 'development') {
                    this.reason = '';
                    this.$store.dispatch('permissionChanged', ['screens', true]);
                    this.watchWindowManagementPermission();
                } else {
                    this.reason = 'Additional screens connected. ';
                    this.$store.dispatch('permissionChanged', ['screens', false]);
                }
            } catch {
                if (showPending) {
                    this.pending = {
                        ...this.pending,
                        screens: false,
                    };
                }
                this.$store.dispatch('permissionChanged', ['screens', false]);
            }
        },
        watchWindowManagementPermission() {
            if (this.windowManagementPermissionStatus) {
                return;
            }
            navigator.permissions
                .query({ name: 'window-management' })
                .then((permissionStatus) => {
                    this.windowManagementPermissionStatus = permissionStatus;
                    permissionStatus.onchange = () => {
                        if (permissionStatus.status !== 'granted' && !this.ended) {
                            this.reason = 'Unable to check screens connected. ';
                            this.$store.dispatch('permissionChanged', ['screens', false]);
                        }
                    };
                })
                .catch(() => {
                    this.reason = 'Unable to check screens connected. ';
                    this.$store.dispatch('permissionChanged', ['screens', false]);
                });
        },
        checkFullscreenPermission() {
            this.$store.dispatch('permissionChanged', ['fullscreen', !!document.fullscreenElement]);
        },
        checkCapturePermission() {
            if (!this.screenCaptureEnabled) {
                this.$store.dispatch('permissionChanged', ['capture', false]);
                return;
            }
            const hasLiveScreenTrack =
                !!this.screenStream &&
                this.screenStream.getVideoTracks().some((track) => track.readyState === 'live');
            if (!hasLiveScreenTrack && !this.ended) {
                this.triggerSecurityFailure('Access to your screen recording revoked. ');
            }
            this.$store.dispatch('permissionChanged', ['capture', hasLiveScreenTrack]);
        },
        checkCameraPermission() {
            if (!this.$store.getters.getCapturePhotos && !this.$store.getters.getAI) {
                return;
            }
            const hasLiveCameraTrack =
                !!this.cameraStream &&
                this.cameraStream.getVideoTracks().some((track) => track.readyState === 'live');
            if (!hasLiveCameraTrack && !this.ended) {
                this.triggerSecurityFailure('Access to your camera revoked. ');
            }
            this.$store.dispatch('permissionChanged', ['camera', hasLiveCameraTrack]);
        },
        screenChange() {
            if (!this.ended) {
                this.reason = 'Additional screens connected. ';
                this.$store.dispatch('permissionChanged', ['screens', false]);
            }
        },
        fullscreen() {
            if (this.$store.state.permissions.browser === false || typeof document.documentElement?.requestFullscreen !== 'function') {
                this.$store.dispatch('permissionChanged', ['fullscreen', false]);
                return;
            }
            this.pending = {
                ...this.pending,
                fullscreen: true,
            };
            document.documentElement
                .requestFullscreen()
                .then(() => {
                    this.pending = {
                        ...this.pending,
                        fullscreen: false,
                    };
                    this.$store.dispatch('permissionChanged', ['fullscreen', true]);
                    document.addEventListener('fullscreenchange', this.fullscreenChange);
                })
                .catch(() => {
                    this.pending = {
                        ...this.pending,
                        fullscreen: false,
                    };
                    this.$store.dispatch('permissionChanged', ['fullscreen', false]);
                });
        },
        fullscreenChange() {
            this.checkFullscreenPermission();
            if (!document.fullscreenElement) {
                this.triggerSecurityFailure('You exited fullscreen. ');
            }
        },
        capture() {
            this.pending = {
                ...this.pending,
                capture: true,
            };
            navigator.mediaDevices
                .getDisplayMedia({
                    video: {
                        cursor: 'always',
                        displaySurface: 'monitor',
                        resizeMode: 'none',
                    },
                    audio: false,
                })
                .then((stream) => {
                    this.screenStream = stream;
                    this.screenTrack = new ImageCapture(stream.getVideoTracks()[0]);
                    this.pending = {
                        ...this.pending,
                        capture: false,
                    };
                    this.$store.dispatch('permissionChanged', ['capture', true]);
                    navigator.permissions.query({ name: 'display-capture' }).then((permissionStatus) => {
                        permissionStatus.onchange = () => {
                            if (permissionStatus.status !== 'granted' && !this.ended) {
                                this.triggerSecurityFailure('Unable to screen record. ');
                                this.$store.dispatch('permissionChanged', ['capture', false]);
                            }
                        };
                    });
                    this.screenStream.getVideoTracks()[0].onended = () => {
                        if (!this.ended) {
                            this.triggerSecurityFailure('Access to your screen recording revoked. ');
                            this.$store.dispatch('permissionChanged', ['capture', false]);
                        }
                    };
                })
                .catch(() => {
                    this.pending = {
                        ...this.pending,
                        capture: false,
                    };
                    this.$store.dispatch('permissionChanged', ['capture', false]);
                });
        },
        camera() {
            this.cameraFailureMessage = null;
            this.pending = {
                ...this.pending,
                camera: true,
            };
            navigator.mediaDevices
                .getUserMedia(this.getCameraConstraints())
                .then((stream) => {
                    this.cameraStream = stream;
                    this.cameraImageCapture = new ImageCapture(stream.getVideoTracks()[0]);
                    this.cameraFailureMessage = null;
                    navigator.mediaDevices.enumerateDevices().then((deviceInfos) => {
                        const activeDeviceId = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
                        this.updateCameraList(deviceInfos, activeDeviceId);
                        this.pending = {
                            ...this.pending,
                            camera: false,
                        };
                        this.$store.dispatch('permissionChanged', ['camera', true]);
                    });
                })
                .catch((error) => {
                    this.handleCameraAccessFailure(error);
                });
        },
        cameraChange(cameraInput) {
            if (!cameraInput || cameraInput === this.selectedCamera) {
                return;
            }
            this.cameraFailureMessage = null;
            this.pending = {
                ...this.pending,
                camera: true,
            };
            this.selectedCamera = cameraInput;
            if (this.cameraStream) {
                this.cameraStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            this.getCameraStream();
        },
        getCameraStream(attempt = 0) {
            if (attempt === 0) {
                this.cameraFailureMessage = null;
            }
            this.cameraConstraints = this.getCameraConstraints();
            navigator.mediaDevices
                .getUserMedia(this.cameraConstraints)
                .then((stream) => {
                    this.cameraStream = stream;
                    this.cameraImageCapture = new ImageCapture(stream.getVideoTracks()[0]);
                    this.cameraFailureMessage = null;
                    const activeDeviceId = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
                    if (activeDeviceId) {
                        this.selectedCamera = activeDeviceId;
                    }
                    navigator.mediaDevices.enumerateDevices().then((deviceInfos) => {
                        this.updateCameraList(deviceInfos, activeDeviceId);
                    });
                    this.$store.dispatch('permissionChanged', ['camera', true]);
                    this.pending = {
                        ...this.pending,
                        camera: false,
                    };
                })
                .catch((error) => {
                    if (error?.name === 'NotReadableError' && attempt === 0) {
                        window.setTimeout(() => this.getCameraStream(1), 300);
                        return;
                    }
                    if (error?.name === 'NotFoundError' && this.selectedCamera && attempt === 0) {
                        this.selectedCamera = null;
                        this.cameraStream = null;
                        this.cameraImageCapture = null;
                        window.setTimeout(() => this.getCameraStream(1), 0);
                        return;
                    }
                    this.handleCameraAccessFailure(error);
                });
        },
        updateCameraList(deviceInfos, preferredDeviceId = null) {
            let cameraCount = 0;
            const cameras = [];

            for (let index = 0; index < deviceInfos.length; index++) {
                if (deviceInfos[index].kind === 'videoinput') {
                    cameraCount++;
                    cameras.push({
                        value: deviceInfos[index].deviceId,
                        text: deviceInfos[index].label || 'Camera ' + cameraCount,
                    });
                }
            }

            this.cameraCount = cameraCount;
            this.cameras = cameras;

            if (!cameras.length) {
                this.selectedCamera = null;
                return;
            }

            const hasSelectedCamera = this.selectedCamera && cameras.some((camera) => camera.value === this.selectedCamera);
            if (preferredDeviceId && cameras.some((camera) => camera.value === preferredDeviceId)) {
                this.selectedCamera = preferredDeviceId;
            } else if (!hasSelectedCamera) {
                this.selectedCamera = cameras[0].value;
            }
        },
        getCameraConstraints() {
            return {
                audio: false,
                video: this.selectedCamera
                    ? {
                        deviceId: { exact: this.selectedCamera },
                    }
                    : true,
            };
        },
        async start() {
            this.loading = true;
            this.wakeLock = await navigator.wakeLock.request('screen');
            AssessmentService.getAssessment(this.$route.params.id).then(async (resp) => {
                const assessment = this.getValidAssessment(resp);
                if (!assessment) {
                    this.loading = false;
                    return;
                }

                this.assessment = {
                    ...this.assessment,
                    ...assessment,
                };

                if ((resp.success && assessment.status === 'Enrolled') || (!this.$store.getters.getSecureAssessment && assessment.status === 'Started')) {
                    let continuation = false;
                    if (!this.$store.getters.getSecureAssessment && assessment.status === 'Started' && this.$store.getters.getAssessment.filter((a) => a.id === this.assessment.id).length) {
                        continuation = true;
                    }
                    if (!continuation) {
                        await AssessmentService.updateStatus(this.assessment.id, 'Started');
                    }
                    if (this.$store.getters.getSecureAssessment) {
                        if (this.captureEvidence) {
                            this.collectEvidence();
                        }
                        this.wakeLock.addEventListener('release', this.examFailure);
                        if (this.$store.getters.getCapturePhotos || this.$store.getters.getAI) {
                            navigator.permissions.query({ name: 'camera' }).then((permissionStatus) => {
                                permissionStatus.onchange = () => {
                                    if (permissionStatus.status !== 'granted' && !this.ended) {
                                        this.triggerSecurityFailure('Access to your camera revoked. ');
                                        this.$store.dispatch('permissionChanged', ['camera', false]);
                                    }
                                };
                            });
                            this.cameraStream.getVideoTracks()[0].onended = () => {
                                if (!this.ended) {
                                    this.triggerSecurityFailure('Access to your camera revoked. ');
                                    this.$store.dispatch('permissionChanged', ['camera', false]);
                                }
                            };
                        }
                        if (process.env.NODE_ENV === 'production'){
                            window.addEventListener('blur', this.handleWindowBlur);
                        }
                    }
                    this.loading = false;
                    if (continuation) {
                        this.question = this.$store.getters.getAssessment.filter((a) => a.id === this.assessment.id)[0].currentQuestion;
                        this.assessment.blocks = JSON.parse(JSON.stringify(this.$store.getters.getAssessment.filter((a) => a.id === this.assessment.id)[0].responses));
                        const start = this.$store.getters.getAssessment.filter((a) => a.id === this.assessment.id)[0].start;
                        this.startTime = DateTime.fromISO(start);
                    } else {
                        this.startTime = DateTime.now();
                        this.$store.dispatch('setAssessment', {
                            id: this.assessment.id,
                            responses: [],
                            currentQuestion: 0,
                            start: this.startTime.toString(),
                        });
                    }
                    this.syncAssessmentProgressState();
                    this.assessmentStarted = true;
                    this.proceed('exam');
                } else {
                    this.$router.push('/assessments');
                }
            }).catch((error) => {
                this.loading = false;
                notifyError(error, {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'start.getAssessment',
                        assessmentId: this.$route.params.id,
                    },
                });
                this.showLoadFailureToast();
                this.$router.push('/assessments');
            });
        },
        getValidAssessment(response) {
            const assessment = response?.assessment;
            const currentSiteId = this.$store.state.site.id;

            if (!assessment) {
                this.showErrorToast('assessmentLoadFailedTitle', 'assessmentLoadFailedText');
                this.$router.push('/assessments');
                return null;
            }

            if (assessment.provider_id === undefined || assessment.provider_id === null || assessment.provider_id === '') {
                Bugsnag.notify(new Error(`Assessment ${assessment.id || this.$route.params.id} missing provider_id`), (event) => {
                    event.addMetadata('assessment', {
                        id: assessment.id,
                        provider_id: assessment.provider_id,
                        course_provider_id: assessment.course_provider_id,
                        status: assessment.status,
                    });
                    event.addMetadata('response', {
                        assessment: response?.assessment || null,
                    });
                });
                this.showErrorToast('assessmentLoadFailedTitle', 'assessmentLoadFailedText');
                this.$router.push('/assessments');
                return null;
            }

            if (currentSiteId !== 0 && assessment.provider_id !== currentSiteId) {
                this.$router.push('/assessments');
                return null;
            }

            return assessment;
        },
        async getUploadError(response) {
            const responseBody = await response.text().catch(() => '');
            const responseSummary = [`Upload failed with status ${response.status}`];

            if (response.statusText) {
                responseSummary.push(response.statusText);
            }

            if (responseBody) {
                responseSummary.push(responseBody.slice(0, 500));
            }

            return new Error(responseSummary.join(': '));
        },
        collectEvidence() {
            if (!this.ended) {
                const promises = [];
                if (this.$store.getters.getCapturePhotos) {
                    promises.push(
                        this.cameraImageCapture.grabFrame().catch((error) => {
                            Bugsnag.notify(error instanceof Error ? error : new Error(error));
                            this.triggerSecurityFailure('Unable to capture a camera image. ');
                            return null;
                        })
                    );
                } else {
                    promises.push(Promise.resolve(null));
                }
                if (this.screenCaptureEnabled) {
                    promises.push(
                        this.screenTrack.grabFrame().catch((error) => {
                            Bugsnag.notify(error instanceof Error ? error : new Error(error));
                            this.triggerSecurityFailure('Unable to capture a screen image. ');
                            return null;
                        })
                    );
                } else {
                    promises.push(Promise.resolve(null));
                }
                Promise.all(promises)
                    .then(([camerabitmap, screenbitmap]) => {
                        if (this.ended) {
                            return;
                        }
                        if (camerabitmap) {
                            this.processEvidence(camerabitmap, 'camera');
                        }
                        if (screenbitmap) {
                            this.processEvidence(screenbitmap, 'screen');
                        }
                        this.interval = setTimeout(() => {
                            this.generateInterval();
                            this.collectEvidence();
                        }, this.evidenceGap);
                    })
                    .catch((error) => {
                        Bugsnag.notify(error instanceof Error ? error : new Error(error));
                    });
            }
        },
        processEvidence(bitmap, type) {
            let canvas = document.createElement('canvas');
            let context = canvas.getContext('2d');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            context.drawImage(bitmap, 0, 0);
            const date = new Date();
            const timestamp = date.getDate() + '' + (date.getMonth() + 1) + '' + date.getFullYear() + 'T' + date.getHours() + '-' + date.getMinutes() + '-' + date.getSeconds();
            canvas.toBlob((blob) => {
                this.zip.folder(type).file(timestamp + '.jpg', blob);
                this.evidenceCapturedCount[type]++;
            });
        },
        toggleNav(show) {
            if (show === undefined) {
                this.showNav = !this.showNav;
                return;
            }
            this.showNav = show;
        },
        goBack() {
            this.question--;
            this.$store.dispatch('setCurrentQuestion', [this.assessment.id, this.question]);
            this.showNav = false;
        },
        goToQuestion(index) {
            this.question = index;
            this.syncAssessmentProgressState();
            this.$store.dispatch('setCurrentQuestion', [this.assessment.id, this.question]);
            this.showNav = false;
        },
        goForward() {
            this.question++;
            this.syncAssessmentProgressState();
            this.$store.dispatch('setCurrentQuestion', [this.assessment.id, this.question]);
        },
        response(question, answer) {
            if (this.question === this.assessment.blocks.length - 1) {
                this.showNav = true;
            }
            if (this.assessment.blocks[question].type === 'free-text') {
                this.assessment.blocks[question].response = answer;
            } else if (this.assessment.blocks[question].type === 'quiz') {
                if (!Array.isArray(answer)) {
                    answer = [answer];
                }
                this.assessment.blocks[question].response = answer;
            }
            this.$store.dispatch('setResponses', [this.assessment.id, JSON.parse(JSON.stringify(this.assessment.blocks))]);
        },
        async end() {
            try {
                clearTimeout(this.interval);
                this.stopSecurityMonitor();
                this.submitting = true;
                this.ended = true;
                if (this.$store.getters.getSecureAssessment) {
                    if (this.screens) {
                        this.screens.removeEventListener('screenschange', this.screenChange);
                    }
                    document.removeEventListener('fullscreenchange', this.fullscreenChange);
                    window.removeEventListener('blur', this.handleWindowBlur);
                    const wakeLock = this.wakeLock;
                    this.wakeLock = null;
                    if (wakeLock) {
                        wakeLock.removeEventListener('release', this.examFailure);
                        try {
                            await wakeLock.release();
                        } catch (error) {
                            Bugsnag.notify(error instanceof Error ? error : new Error(error));
                        }
                    }
                    if (document.fullscreenElement) {
                        document.exitFullscreen();
                    }
                    if (this.$store.getters.getAI) {
                        this.aiService.stop();
                    }
                    if (this.$store.getters.getCapturePhotos || this.$store.getters.getAI) {
                        this.cameraStream.getTracks().forEach((track) => {
                            track.stop();
                        });
                    }
                    if (this.screenCaptureEnabled && this.screenStream) {
                        this.screenStream.getTracks().forEach((track) => {
                            track.stop();
                        });
                    }
                    this.$store.dispatch('resetPermissions');
                } else {
                    this.$store.dispatch('resetPermissions');
                }
            } catch (error) {
                Bugsnag.notify(error);
            }
        },
        clearAssessmentSession() {
            this.$store.dispatch('clearAssessment', this.assessment.id);
            this.$store.dispatch('resetPermissions');
        },
        markFailureToSubmit() {
            if (this.failureToSubmitMarked || this.submissionComplete || this.section !== 'outro' || !this.ended || !this.assessment?.id) {
                return;
            }

            this.failureToSubmitMarked = true;
            this.clearAssessmentSession();
            AssessmentService.updateStatus(this.assessment.id, 'Exited', 'FailureToSubmit').catch((error) => {
                notifyError(error, {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'markFailureToSubmit',
                        assessmentId: this.assessment.id,
                    },
                });
            });
        },
        async completeAssessmentSubmission() {
            if (this.submitting || this.submissionComplete) {
                return;
            }

            this.section = 'outro';
            this.submitting = true;
            this.submissionError = false;

            if (!this.ended) {
                await this.end();
            }

            const submitted = await this.sendData();
            this.submitting = false;

            if (submitted) {
                this.submissionComplete = true;
                this.submissionError = false;
                this.submitResponse = null;
                this.clearAssessmentSession();
                return;
            }

            this.submissionComplete = false;
            this.submissionError = true;
            this.failureToSubmitMarked = false;
        },
        retryAssessmentSubmission() {
            this.completeAssessmentSubmission();
        },
        formatResponse(block) {
            if (block.response) {
                return block.response;
            }
            if (block.type === 'free-text') {
                return '';
            } else if (block.type === 'quiz') {
                return [];
            }
            return null;
        },
        async sendData() {
            let zipBlob;
            let zipFile;
            if (this.captureEvidence) {
                zipBlob = await this.zip.generateAsync({ type: 'blob' });
                zipFile = new File([zipBlob], 'security.zip');
            }
            const formData = new FormData();
            let responseData = [];
            this.assessment.blocks.forEach((block) => {
                responseData.push({
                    id: block.id,
                    type: block.type,
                    response: this.formatResponse(block),
                });
            });
            if (this.captureEvidence) {
                const fileName = `${this.assessment.provider_id}/${this.user.id}/${this.assessment.id}/${Date.now()}.zip`;
                formData.append('key', fileName);
            }
            formData.append('blocks', JSON.stringify(responseData));
            try {
                const response = this.submitResponse || await AssessmentService.submitAssessment(this.assessment.id, formData);
                this.submitResponse = response;
                if (response.success && this.captureEvidence && response.preSignedUrl) {
                    if (process.env.NODE_ENV === 'production') {
                        try {
                            const awsResponse = await fetch(response.preSignedUrl, {
                                method: 'PUT',
                                body: zipFile,
                            });
                            if (!awsResponse.ok) {
                                throw await this.getUploadError(awsResponse);
                            }

                            await AssessmentService.updateStatus(this.assessment.id, 'Submitted with Evidence');
                            return true;
                        } catch (error) {
                            await AssessmentService.updateStatus(this.assessment.id, 'Submitted without Evidence');
                            Bugsnag.notify(error, (event) => {
                                event.addMetadata('Response Data', {
                                    zipFileSize: zipBlob.size,
                                    ...this.evidenceCapturedCount,
                                    timeOfFailure: Date.now().toString(),
                                });
                            });
                            return false;
                        }
                    } else {
                        const url = window.URL.createObjectURL(zipBlob);
                        const a = document.createElement('a');
                        a.style.display = 'none';
                        a.href = url;
                        a.download = 'security.zip';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        await AssessmentService.updateStatus(this.assessment.id, 'Submitted with Evidence');
                        return true;
                    }
                } else if (response.success && !this.captureEvidence) {
                    await AssessmentService.updateStatus(this.assessment.id, 'Submitted');
                    return true;
                } else {
                    throw response;
                }
            } catch (error) {
                notifyError(error, {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'sendData',
                        assessmentId: this.assessment.id,
                    },
                    responseData,
                });
                return false;
            }
        },
        finish() {
            window.onbeforeunload = null;
            this.$router.push('/assessments');
        },
        cameraIssue() {
            this.triggerSecurityFailure('We detected an issue with your camera. ');
        },
        handleWindowBlur() {
            this.triggerSecurityFailure('We detected you switched away from the assessment. ');
        },
        triggerSecurityFailure(reason) {
            if (this.ended || !this.assessmentStarted) {
                return;
            }
            this.reason = reason;
            this.examFailure();
        },
        async examFailure() {
            if (!this.ended) {
                this.ended = true;
                this.submitting = true;
                if (!this.reason) {
                    this.reason = 'We detected you switched away from the assessment. ';
                }
                await this.end();
                try {
                    await AssessmentService.updateStatus(this.assessment.id, 'Exited', 'Security');
                } catch (error) {
                    notifyError(error, {
                        source: {
                            screen: 'AssessmentScreen',
                            action: 'examFailure.updateStatus',
                            assessmentId: this.assessment.id,
                            reason: this.reason,
                        },
                    });
                }
                this.clearAssessmentSession();
                this.exited = true;
            }
        },
        generateInterval() {
            const min = 30000;
            const max = this.$store.getters.getMaxTimeout;
            this.evidenceGap = Math.floor(Math.random() * (max - min + 1)) + min;
        },
        async baseFace(captureSource) {
            this.aiStartError = null;
            this.aiStartReady = false;
            let result;
            const video = captureSource?.video || captureSource;
            const capturedFrame = captureSource?.capturedFrame || null;

            try {
                result = await this.aiService.captureBaseFace(video, capturedFrame);
            } catch (error) {
                Bugsnag.notify(error instanceof Error ? error : new Error(error));
                this.aiStartError = 'errorAiSetupFailed';
                this.showAiSetupFailureToast();
                return;
            }

            if (result.error) {
                this.aiStartError = result.error;
                if (result.error === 'errorAiSetupFailed') {
                    this.showAiSetupFailureToast();
                }
            } else if (result.ready) {
                const uploadFace = async (faceUrl) => {
                    return new Promise((resolve, reject) => {
                        result.baseFace.toBlob(async (blob) => {
                            try {
                                const awsResponse = await fetch(faceUrl, {
                                    method: 'PUT',
                                    body: blob,
                                });
                                if (awsResponse.ok) {
                                    resolve(true);
                                } else {
                                    reject(awsResponse);
                                }
                            } catch (error) {
                                reject(error);
                            }
                        }, 'image/jpeg');
                    });
                };
                try {
                    await uploadFace(this.assessment.faceUrl);
                    this.aiBaseFace = result.baseFaceSrc;
                    this.aiStartReady = true;
                } catch (error) {
                    console.error(error);
                    try {
                        const response = await AssessmentService.getAssessment(this.assessment.id);
                        if (response && response.assessment && response.assessment.faceUrl) {
                            this.assessment.faceUrl = response.assessment.faceUrl;
                            await uploadFace(this.assessment.faceUrl);
                            this.aiBaseFace = result.baseFaceSrc || result.baseFace.toDataURL('image/png');
                            this.aiStartReady = true;
                        } else {
                            throw new Error(response?.status || 'Missing face upload URL');
                        }
                    } catch (err) {
                        Bugsnag.notify(err instanceof Error ? err : new Error(err));
                        this.aiStartError = 'errorAiSetupFailed';
                        this.showAiSetupFailureToast();
                    }
                }
            }
        },
        async startAI(video) {
            this.aiService.setVideoElement(video);
            try {
                await this.aiService.start();
            } catch (error) {
                Bugsnag.notify(error instanceof Error ? error : new Error(error));
                this.aiStartError = 'errorAiSetupFailed';
                this.showAiSetupFailureToast();
            }
        },
        async flagItUp(flag) {
            let response;

            try {
                response = await AssessmentService.submitFlag(this.assessment.id, flag.type);
            } catch (error) {
                notifyError(error, {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'flagItUp.submitFlag',
                        assessmentId: this.assessment.id,
                        flagType: flag.type,
                    },
                });
                return;
            }

            if (!response?.preSignedUrl) {
                notifyError(new Error('Flag upload missing preSignedUrl'), {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'flagItUp.missingPreSignedUrl',
                        assessmentId: this.assessment.id,
                        flagType: flag.type,
                    },
                    response,
                });
                return;
            }

            const blob = await new Promise((resolve) => {
                flag.preview?.toBlob(resolve, 'image/jpeg');
            });

            if (!blob) {
                notifyError(new Error('Flag upload failed to create preview blob'), {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'flagItUp.missingPreviewBlob',
                        assessmentId: this.assessment.id,
                        flagType: flag.type,
                    },
                });
                return;
            }

            try {
                const awsResponse = await fetch(response.preSignedUrl, {
                    method: 'PUT',
                    body: blob,
                });
                if (!awsResponse.ok) {
                    throw await this.getUploadError(awsResponse);
                }
            } catch (error) {
                notifyError(error, {
                    source: {
                        screen: 'AssessmentScreen',
                        action: 'flagItUp.uploadEvidence',
                        assessmentId: this.assessment.id,
                        flagType: flag.type,
                    },
                });
            }
        },
    },
};
</script>
